import Button from "../components/Common/Button";
import Input from "../components/Common/Input";
import ToastMessage from "../components/Common/ToastMessage";
import { useContext, useEffect, useState } from "react";
import { forgotPassword, updateUserPreference } from "../lib/api";
import CurrentUserContext from "../contexts/CurrentUserContext";
import Label from "../components/Common/Label";
import { getUserDisplayName } from "../utils/common_utils";

const ENERGY_UNITS = ["KWH", "BTU"];
const US_PHONE_DIGIT_LENGTH = 10;
const OTP_CODE_MAX_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;
const MAX_VERIFICATION_ATTEMPTS = 5;

const getPhoneDigits = (value = "") => value.replace(/\D/g, "");
const getUsPhoneDigits = (value = "") => {
    const digits = getPhoneDigits(value);

    if (digits.length === US_PHONE_DIGIT_LENGTH + 1 && digits.startsWith("1")) {
        return digits.slice(1);
    }

    return digits;
};

const formatUsPhoneNumber = (value = "") => {
    const digits = getUsPhoneDigits(value);

    if (digits.length <= 3) {
        return digits;
    }

    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const validateUsPhoneNumber = (value = "") => {
    const digits = getUsPhoneDigits(value);

    if (!digits) {
        return "Phone number is required when SMS alerts are enabled.";
    }

    if (digits.length !== US_PHONE_DIGIT_LENGTH) {
        return "Enter a valid US phone number.";
    }

    return "";
};

const formatPhoneNumberForApi = (value = "") => {
    const digits = getUsPhoneDigits(value);

    if (digits.length !== US_PHONE_DIGIT_LENGTH) {
        return digits;
    }

    return `+1${digits}`;
};

const getOtpDigits = (value = "") => value.replace(/\D/g, "").slice(0, OTP_CODE_MAX_LENGTH);

const validateSmsVerificationCode = (value = "") => {
    const digits = getOtpDigits(value);

    if (!digits) {
        return "Verification code is required.";
    }

    if (digits.length !== OTP_CODE_MAX_LENGTH) {
        return "Enter the 6-digit verification code.";
    }

    return "";
};

const getSmsPreferenceSnapshot = (user) => ({
    smsNotificationsEnabled: Boolean(user?.smsNotificationsEnabled),
    phoneNumber: formatUsPhoneNumber(user?.phoneNumber || ""),
});

const getSmsToggleLabel = (isEnabled) => `SMS alerts ${isEnabled ? "enabled" : "disabled"}`;
const getUserPreferenceResponseData = (response) => response?.data?.data || response?.data || {};

export default function MyAccount() {
    const { currentUser, updateCurrentUser } = useContext(CurrentUserContext);
    const [energyUnit, setEnergyUnit] = useState(currentUser?.energyUnit || "KWH");
    const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(Boolean(currentUser?.emailNotificationsEnabled));
    const [smsNotificationsEnabled, setSmsNotificationsEnabled] = useState(Boolean(currentUser?.smsNotificationsEnabled));
    const [phoneNumber, setPhoneNumber] = useState(formatUsPhoneNumber(currentUser?.phoneNumber || ""));
    const [smsVerificationCode, setSmsVerificationCode] = useState("");
    const [smsVerificationRequired, setSmsVerificationRequired] = useState(false);
    const [smsVerificationAttempts, setSmsVerificationAttempts] = useState(0);
    const [smsResendCooldownSeconds, setSmsResendCooldownSeconds] = useState(0);
    const [smsPhoneError, setSmsPhoneError] = useState("");
    const [smsVerificationError, setSmsVerificationError] = useState("");
    const [isSavingEnergyUnit, setIsSavingEnergyUnit] = useState(false);
    const [isSavingEmailNotifications, setIsSavingEmailNotifications] = useState(false);
    const [isSavingSmsPreferences, setIsSavingSmsPreferences] = useState(false);
    const [toast, setToast] = useState({
        show: false,
        message: "",
        type: "success",
    });

    const initialSmsPreferenceSnapshot = getSmsPreferenceSnapshot(currentUser);
    const formattedCurrentPhoneNumber = formatUsPhoneNumber(currentUser?.phoneNumber || "");
    const isSmsPhoneNumberValid = validateUsPhoneNumber(phoneNumber) === "";
    const isSmsVerificationCodeValid = validateSmsVerificationCode(smsVerificationCode) === "";
    const hasSmsChanges =
        smsNotificationsEnabled !== initialSmsPreferenceSnapshot.smsNotificationsEnabled ||
        phoneNumber !== initialSmsPreferenceSnapshot.phoneNumber;
    const isSmsResendAvailable =
        smsVerificationRequired &&
        smsResendCooldownSeconds === 0 &&
        !isSavingSmsPreferences;
    const hasRemainingVerificationAttempts =
        smsVerificationAttempts < MAX_VERIFICATION_ATTEMPTS;
    const shouldShowPhoneNumberField =
        smsNotificationsEnabled || smsVerificationRequired;
    const canSubmitSmsVerification =
        !isSavingSmsPreferences &&
        hasRemainingVerificationAttempts &&
        isSmsPhoneNumberValid &&
        isSmsVerificationCodeValid;
    const canSaveSmsPreferences = (() => {
        if (isSavingSmsPreferences) {
            return false;
        }

        if (smsVerificationRequired) {
            return false;
        }

        if (!smsNotificationsEnabled) {
            return hasSmsChanges;
        }

        return hasSmsChanges && isSmsPhoneNumberValid;
    })();

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        setEnergyUnit(currentUser?.energyUnit || "KWH");
    }, [currentUser?.energyUnit]);

    useEffect(() => {
        setEmailNotificationsEnabled(Boolean(currentUser?.emailNotificationsEnabled));
    }, [currentUser?.emailNotificationsEnabled]);

    useEffect(() => {
        setSmsNotificationsEnabled(Boolean(currentUser?.smsNotificationsEnabled));
        setPhoneNumber(formattedCurrentPhoneNumber);
        setSmsVerificationCode("");
        setSmsVerificationRequired(false);
        setSmsVerificationAttempts(0);
        setSmsResendCooldownSeconds(0);
        setSmsPhoneError("");
        setSmsVerificationError("");
    }, [currentUser?.smsNotificationsEnabled, formattedCurrentPhoneNumber]);

    useEffect(() => {
        if (smsResendCooldownSeconds <= 0) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            setSmsResendCooldownSeconds((previousSeconds) => {
                if (previousSeconds <= 1) {
                    window.clearInterval(timer);
                    return 0;
                }

                return previousSeconds - 1;
            });
        }, 1000);

        return () => window.clearInterval(timer);
    }, [smsResendCooldownSeconds]);

    const handleForgotPassword = async (e) => {
        e.preventDefault();

        const payload = {
            email: currentUser?.email,
        };

        try {
            const res = await forgotPassword(payload);

            if (res.status === 200) {
                showToast("Reset Password request has been sent to your email.", "success");
            } else {
                showToast(res.message || "Change Password request failed. Please try again", "failed");
            }
        } catch (err) {
            showToast("Network error. Please try again", "failed");
        }
    };

    const handleEnergyUnitChange = async (selectedEnergyUnit) => {
        if (selectedEnergyUnit === energyUnit || isSavingEnergyUnit) {
            return;
        }

        const previousEnergyUnit = energyUnit;

        setEnergyUnit(selectedEnergyUnit);
        updateCurrentUser({ energyUnit: selectedEnergyUnit });
        setIsSavingEnergyUnit(true);

        try {
            await updateUserPreference({ energyUnit: selectedEnergyUnit });
            showToast("Energy unit preference updated successfully.", "success");
        } catch (err) {
            if (err.status === 401 || err.status === 403) {
                return;
            }

            setEnergyUnit(previousEnergyUnit);
            updateCurrentUser({ energyUnit: previousEnergyUnit });
            showToast(err.message || "Failed to update energy unit preference.", "failed");
        } finally {
            setIsSavingEnergyUnit(false);
        }
    };

    const handleEmailNotificationsToggle = async () => {
        if (isSavingEmailNotifications) {
            return;
        }

        const previousEmailNotificationsEnabled = emailNotificationsEnabled;
        const nextEmailNotificationsEnabled = !emailNotificationsEnabled;

        setEmailNotificationsEnabled(nextEmailNotificationsEnabled);
        updateCurrentUser({ emailNotificationsEnabled: nextEmailNotificationsEnabled });
        setIsSavingEmailNotifications(true);

        try {
            await updateUserPreference({ emailNotificationsEnabled: nextEmailNotificationsEnabled });
            showToast("Email notifications preference updated successfully.", "success");
        } catch (err) {
            if (err.status === 401 || err.status === 403) {
                return;
            }

            setEmailNotificationsEnabled(previousEmailNotificationsEnabled);
            updateCurrentUser({ emailNotificationsEnabled: previousEmailNotificationsEnabled });
            showToast(err.message || "Failed to update email notifications preference.", "failed");
        } finally {
            setIsSavingEmailNotifications(false);
        }
    };

    const handleSmsNotificationsToggle = () => {
        const nextValue = !smsNotificationsEnabled;

        setSmsNotificationsEnabled(nextValue);
        setSmsVerificationRequired(false);
        setSmsVerificationCode("");
        setSmsVerificationError("");
        setSmsVerificationAttempts(0);
        setSmsResendCooldownSeconds(0);

        if (!nextValue) {
            setSmsPhoneError("");
            return;
        }

        if (phoneNumber) {
            setSmsPhoneError(validateUsPhoneNumber(phoneNumber));
        }
    };

    const handlePhoneNumberChange = (e) => {
        const formattedPhoneNumber = formatUsPhoneNumber(e.target.value);

        setPhoneNumber(formattedPhoneNumber);

        if (smsPhoneError) {
            setSmsPhoneError(validateUsPhoneNumber(formattedPhoneNumber));
        }

        if (smsVerificationRequired) {
            setSmsVerificationRequired(false);
            setSmsVerificationCode("");
            setSmsVerificationError("");
            setSmsVerificationAttempts(0);
            setSmsResendCooldownSeconds(0);
        }
    };

    const handlePhoneNumberBlur = () => {
        if (!smsNotificationsEnabled) {
            return;
        }

        setSmsPhoneError(validateUsPhoneNumber(phoneNumber));
    };

    const handleSmsVerificationCodeChange = (e) => {
        const code = getOtpDigits(e.target.value);
        setSmsVerificationCode(code);

        if (smsVerificationError) {
            setSmsVerificationError(validateSmsVerificationCode(code));
        }
    };

    const handleSmsVerificationCodeBlur = () => {
        if (!smsVerificationRequired) {
            return;
        }

        setSmsVerificationError(validateSmsVerificationCode(smsVerificationCode));
    };

    const resetSmsVerificationState = () => {
        setSmsVerificationRequired(false);
        setSmsVerificationCode("");
        setSmsVerificationError("");
        setSmsVerificationAttempts(0);
        setSmsResendCooldownSeconds(0);
    };

    const handleEditPhoneNumber = () => {
        if (isSavingSmsPreferences) {
            return;
        }

        resetSmsVerificationState();
    };

    const syncSmsUserStateFromResponse = (responseData, fallbackPhoneNumber, options = {}) => {
        const { preservePendingVerification = false } = options;
        const updatedUser = responseData?.user;
        const nextSmsNotificationsEnabled = preservePendingVerification
            ? true
            : (updatedUser?.smsNotificationsEnabled ?? smsNotificationsEnabled);
        const nextPhoneNumber = formatUsPhoneNumber(
            updatedUser?.phoneNumber ?? fallbackPhoneNumber ?? phoneNumber
        );

        setSmsNotificationsEnabled(Boolean(nextSmsNotificationsEnabled));
        setPhoneNumber(nextPhoneNumber);

        if (!preservePendingVerification) {
            updateCurrentUser({
                smsNotificationsEnabled: Boolean(nextSmsNotificationsEnabled),
                phoneNumber: getPhoneDigits(nextPhoneNumber),
            });
        }
    };

    const requestSmsVerificationCode = async (isResend = false) => {
        const phoneValidationError = validateUsPhoneNumber(phoneNumber);

        if (phoneValidationError) {
            setSmsPhoneError(phoneValidationError);
            return;
        }

        setSmsPhoneError("");
        setSmsVerificationError("");
        setIsSavingSmsPreferences(true);

        try {
            const response = await updateUserPreference({
                smsNotificationsEnabled: true,
                phoneNumber: formatPhoneNumberForApi(phoneNumber),
            });
            const responseData = getUserPreferenceResponseData(response);
            const verificationRequired = Boolean(responseData?.smsVerificationRequired);

            if (verificationRequired) {
                syncSmsUserStateFromResponse(responseData, phoneNumber, {
                    preservePendingVerification: true,
                });
                setSmsVerificationRequired(true);
                setSmsVerificationCode("");
                setSmsResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
                showToast(
                    isResend
                        ? "A new verification code has been sent to your phone."
                        : "Verification Text sent to your phone number successfully.",
                    "success"
                );
                return;
            }

            syncSmsUserStateFromResponse(responseData, phoneNumber);
            resetSmsVerificationState();
            showToast("SMS notifications preference updated successfully.", "success");
        } catch (err) {
            if (err.status === 401 || err.status === 403) {
                return;
            }

            const fallbackMessage = isResend
                ? "Failed to resend verification code."
                : "Failed to send verification code.";
            showToast(err.message || fallbackMessage, "failed");
        } finally {
            setIsSavingSmsPreferences(false);
        }
    };

    const handleSmsPreferencesSave = async () => {
        if (smsVerificationRequired) {
            const phoneValidationError = validateUsPhoneNumber(phoneNumber);
            const otpValidationError = validateSmsVerificationCode(smsVerificationCode);

            setSmsPhoneError(phoneValidationError);
            setSmsVerificationError(otpValidationError);

            if (phoneValidationError || otpValidationError) {
                return;
            }

            setIsSavingSmsPreferences(true);

            try {
                const response = await updateUserPreference({
                    smsNotificationsEnabled: true,
                    phoneNumber: formatPhoneNumberForApi(phoneNumber),
                    smsVerificationCode: getOtpDigits(smsVerificationCode),
                });
                const responseData = getUserPreferenceResponseData(response);

                syncSmsUserStateFromResponse(responseData, phoneNumber);
                resetSmsVerificationState();
                showToast("Verification code verified successfully. SMS alerts have been enabled.", "success");
            } catch (err) {
                if (err.status === 401 || err.status === 403) {
                    return;
                }

                const nextAttemptCount = smsVerificationAttempts + 1;

                if (nextAttemptCount >= MAX_VERIFICATION_ATTEMPTS) {
                    resetSmsVerificationState();
                    showToast(
                        err.message || "Maximum verification attempts reached. Please request a new verification code.",
                        "failed"
                    );
                    return;
                }

                setSmsVerificationAttempts(nextAttemptCount);
                setSmsVerificationError(err.message || "Failed to verify SMS code.");
            } finally {
                setIsSavingSmsPreferences(false);
            }

            return;
        }

        if (!smsNotificationsEnabled) {
            setIsSavingSmsPreferences(true);

            try {
                const response = await updateUserPreference({
                    smsNotificationsEnabled: false,
                });
                const responseData = getUserPreferenceResponseData(response);

                syncSmsUserStateFromResponse(responseData, "");
                resetSmsVerificationState();
                setPhoneNumber("");
                updateCurrentUser({
                    smsNotificationsEnabled: false,
                    phoneNumber: "",
                });
                showToast("SMS notifications preference updated successfully.", "success");
            } catch (err) {
                if (err.status === 401 || err.status === 403) {
                    return;
                }

                showToast(err.message || "Failed to update SMS notifications preference.", "failed");
            } finally {
                setIsSavingSmsPreferences(false);
            }

            return;
        }

        await requestSmsVerificationCode(false);
    };

    const handleResendCode = async () => {
        if (!isSmsResendAvailable) {
            return;
        }

        await requestSmsVerificationCode(true);
    };

    const getResendButtonLabel = () => {
        if (smsResendCooldownSeconds > 0) {
            return `Resend code in ${smsResendCooldownSeconds}s`;
        }

        return "Resend code";
    };

    return (
        <>
            <div className="titleAndFilters">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between items-start sm:items-center flex-wrap md:flex-nowrap">
                    <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]">
                        My Account
                    </h2>
                </div>
            </div>
            <div className="myAccountDetails mt-12 flex justify-start pl-0 lg:pl-20 xl:pl-28">
                <div className="grid w-full max-w-[1180px] grid-cols-1 gap-10 lg:grid-cols-[minmax(260px,0.9fr)_minmax(360px,1.15fr)_minmax(420px,1.35fr)] lg:items-start lg:gap-10 xl:gap-12">
                    <div className="flex flex-col gap-5 shrink-0">
                        <div className="userAvatar mx-auto">
                            <svg xmlns="http://www.w3.org/2000/svg" width="104" height="104" viewBox="0 0 104 104" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M104 52C104 67.6695 97.0692 81.7203 86.1059 91.2539C76.9759 99.1933 65.0493 104 52 104C38.9507 104 27.0241 99.1933 17.8941 91.2539C6.9308 81.7202 0 67.6695 0 52C0 23.2812 23.2812 0 52 0C80.7188 0 104 23.2812 104 52ZM67.6 36.4C67.6 27.7844 60.6156 20.8 52 20.8C43.3844 20.8 36.4 27.7844 36.4 36.4C36.4 45.0156 43.3844 52 52 52C60.6156 52 67.6 45.0156 67.6 36.4ZM52 62.4C60.9062 62.4 66.7629 67.1665 70.8926 72.4645C73.9738 76.4174 72.9133 82.2158 68.4435 84.4829C63.5044 86.9881 57.9171 88.4 52 88.4C46.0829 88.4 40.4956 86.9881 35.5565 84.4829C31.0867 82.2158 30.0262 76.4174 33.1074 72.4645C37.237 67.1665 43.0938 62.4 52 62.4Z" fill="#CCCCCC" />
                            </svg>
                        </div>
                        <div className="userDetails flex flex-col gap-1 items-center">
                            <p data-testid="myaccount-user-name" className="text-dark-grey dark:text-white text-lg lg:text-xl font-semibold leading-6 tracking-[0.036px]">
                                {getUserDisplayName(currentUser)}
                            </p>
                            <p data-testid="myaccount-user-email" className="text-dark-grey dark:text-white text-sm font-medium leading-5 tracking-[0.021px] opacity-50">
                                {currentUser?.email}
                            </p>
                            <p data-testid="myaccount-customer-name" className="text-dark-grey dark:text-white text-sm font-normal leading-5 tracking-[0.021px] opacity-50">
                                {currentUser?.customer?.name}
                            </p>
                        </div>
                    </div>

                    <section className="border-t border-[#7DA1C433] pt-8 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0 xl:pl-12">
                        <h5 className="text-black dark:text-white text-lg lg:text-xl font-semibold leading-5 tracking-[0.021px]">Reset Password</h5>
                        <ul className="list list-disc pl-4 space-y-5 mt-6 *:dark:text-white">
                            <li>To reset your password, click the “Reset Password” button below. We’ll send a secure reset link to your registered email address.</li>
                            <li>Open your inbox, find the email, and follow the link to create a new password.</li>
                            <li>Make sure to check your spam folder if you don’t see it right away.</li>
                        </ul>
                        <Button
                            className="bg-theme mt-10 px-5 py-3 text-white text-base font-semibold leading-5 tracking-[-0.32px] rounded-md cursor-pointer w-fit min-w-44 max-[530px]:w-full h-10 flex items-center justify-center hover:bg-theme/80 transition-all duration-300 ease-in-out"
                            onClick={handleForgotPassword}
                        >
                            Reset Password
                        </Button>
                    </section>

                    <section className="border-t border-[#7DA1C433] pt-8 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0 xl:pl-12">
                        <h5 className="text-black dark:text-white text-lg lg:text-xl font-semibold leading-5 tracking-[0.021px]">Preferences</h5>
                        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
                            <p className="text-dark-grey dark:text-white text-base font-semibold leading-5 tracking-[0.021px]">Energy Unit</p>
                            <div className={`flex w-full shrink-0 rounded-full bg-[#E6EEF3] dark:bg-darkThemeModalBg p-1 sm:w-fit ${isSavingEnergyUnit ? "opacity-70" : ""}`}>
                                {ENERGY_UNITS.map((unit) => {
                                    const isSelected = energyUnit === unit;

                                    return (
                                        <Button
                                            key={unit}
                                            className={`h-9 min-w-20 rounded-full px-5 text-sm font-semibold leading-5 transition-all duration-200 ${
                                                isSelected
                                                    ? "bg-theme text-white shadow-appShadow"
                                                    : "text-dark-grey dark:text-white hover:bg-white/70 dark:hover:bg-white/10"
                                            } ${isSavingEnergyUnit ? "cursor-not-allowed" : "cursor-pointer"}`}
                                            onClick={() => handleEnergyUnitChange(unit)}
                                            disabled={isSavingEnergyUnit}
                                            aria-pressed={isSelected}
                                        >
                                            {unit}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mt-10 border-t border-[#7DA1C433] pt-10">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
                                <p className="text-dark-grey dark:text-white text-base font-semibold leading-5 tracking-[0.021px]">Get emails for alerts</p>
                                <Button
                                    className={`relative h-10 w-[92px] shrink-0 rounded-full transition-colors duration-300 ${
                                        emailNotificationsEnabled ? "bg-theme" : "bg-[#E6EEF3] dark:bg-darkThemeModalBg"
                                    } ${isSavingEmailNotifications ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                                    onClick={handleEmailNotificationsToggle}
                                    disabled={isSavingEmailNotifications}
                                    aria-pressed={emailNotificationsEnabled}
                                    aria-label={`Email notifications ${emailNotificationsEnabled ? "enabled" : "disabled"}`}
                                >
                                    <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold transition-opacity duration-300 ${emailNotificationsEnabled ? "text-white opacity-100" : "text-dark-grey dark:text-white opacity-0"}`}>ON</span>
                                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold transition-opacity duration-300 ${emailNotificationsEnabled ? "text-white opacity-0" : "text-dark-grey dark:text-white opacity-100"}`}>OFF</span>
                                    <div className={`absolute top-1 left-1 h-8 w-8 rounded-full bg-white shadow-appShadow transition-transform duration-300 ${emailNotificationsEnabled ? "translate-x-[52px]" : "translate-x-0"}`} />
                                </Button>
                            </div>
                        </div>

                        <div className="mt-10 border-t border-[#7DA1C433] pt-10">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
                                <p className="text-dark-grey dark:text-white text-base font-semibold leading-5 tracking-[0.021px]">Get text messages for alerts</p>
                                <Button
                                    className={`relative h-10 w-[92px] shrink-0 rounded-full transition-colors duration-300 ${
                                        smsNotificationsEnabled ? "bg-theme" : "bg-[#E6EEF3] dark:bg-darkThemeModalBg"
                                    } ${isSavingSmsPreferences ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                                    onClick={handleSmsNotificationsToggle}
                                    disabled={isSavingSmsPreferences}
                                    data-testid="sms-alerts-toggle"
                                    aria-pressed={smsNotificationsEnabled}
                                    aria-label={getSmsToggleLabel(smsNotificationsEnabled)}
                                >
                                    <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold transition-opacity duration-300 ${smsNotificationsEnabled ? "text-white opacity-100" : "text-dark-grey dark:text-white opacity-0"}`}>ON</span>
                                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold transition-opacity duration-300 ${smsNotificationsEnabled ? "text-white opacity-0" : "text-dark-grey dark:text-white opacity-100"}`}>OFF</span>
                                    <div className={`absolute top-1 left-1 h-8 w-8 rounded-full bg-white shadow-appShadow transition-transform duration-300 ${smsNotificationsEnabled ? "translate-x-[52px]" : "translate-x-0"}`} />
                                </Button>
                            </div>

                            {shouldShowPhoneNumberField && (
                                <div className="mt-8">
                                    <Label htmlFor="sms-phone-number" className="text-dark-grey dark:text-white text-base font-semibold leading-5 tracking-[0.021px]">
                                        Phone number
                                    </Label>
                                    <Input
                                        id="sms-phone-number"
                                        data-testid="sms-phone-number-input"
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="tel-national"
                                        value={phoneNumber}
                                        onChange={handlePhoneNumberChange}
                                        onBlur={handlePhoneNumberBlur}
                                        placeholder="(555) 555-5555"
                                        required={smsNotificationsEnabled}
                                        disabled={isSavingSmsPreferences}
                                        className={`mt-4 h-12 w-full rounded-xl border bg-white px-4 text-base text-dark-grey shadow-sm outline-none transition-all duration-200 placeholder:text-[#98A2B3] disabled:cursor-not-allowed disabled:opacity-70 dark:bg-darkThemeModalBg dark:text-white ${
                                            smsPhoneError
                                                ? "border-[#D92D20] focus:border-[#D92D20] focus:ring-2 focus:ring-[#D92D20]/15"
                                                : "border-[#D0D5DD] focus:border-theme focus:ring-2 focus:ring-[#FF8C65]/20"
                                        }`}
                                    />
                                    {smsPhoneError ? (
                                        <p className="mt-2 text-sm font-medium text-[#D92D20]" data-testid="sms-phone-error">{smsPhoneError}</p>
                                    ) : (
                                        <p className="mt-3 text-sm text-dark-grey/70 dark:text-white/60">Message and data rates may apply.</p>
                                    )}
                                </div>
                            )}

                            <Button
                                className={`bg-theme mt-6 px-5 py-3 text-white text-base font-semibold leading-5 tracking-[-0.32px] rounded-md w-fit min-w-28 h-10 flex items-center justify-center transition-all duration-300 ease-in-out ${
                                    canSaveSmsPreferences
                                        ? "cursor-pointer hover:bg-theme/80"
                                        : "cursor-not-allowed opacity-70"
                                }`}
                                onClick={handleSmsPreferencesSave}
                                disabled={!canSaveSmsPreferences}
                                data-testid="sms-save-button"
                            >
                                Save
                            </Button>
                        </div>
                    </section>
                </div>
            </div>

            <ToastMessage
                message={toast.message}
                type={toast.type}
                show={toast.show}
                onClose={() => setToast((prev) => ({ ...prev, show: false }))}
            />

            {smsVerificationRequired && (
                <div className="overlayHolder bg-black/30 dark:bg-black/60 fixed inset-0 flex justify-center items-center z-[999] px-4">
                    <div className="modalHlder bg-white dark:bg-darkThemeModalBg dark:shadow-darkThemeModalShadow rounded-2xl border border-black/10 w-full max-w-md p-6 sm:p-8">
                        <div className="modalContent">
                            <div className="flex flex-col gap-2">
                                <h2 className="text-dark-grey dark:text-white text-xl leading-6 sm:text-2xl sm:leading-7 font-semibold tracking-[0.036px]">
                                    Verify Phone Number
                                </h2>
                                <p className="text-sm leading-5 text-dark-grey/70 dark:text-white/70">
                                    Enter the verification code sent to {phoneNumber || "your phone number"} to enable SMS alerts.
                                </p>
                            </div>

                            <div className="mt-6">
                                <Label htmlFor="sms-verification-code-modal" className="text-dark-grey dark:text-white text-base font-semibold leading-5 tracking-[0.021px]">
                                    Verification code
                                </Label>
                                <Input
                                    id="sms-verification-code-modal"
                                    data-testid="sms-verification-code-input"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    value={smsVerificationCode}
                                    onChange={handleSmsVerificationCodeChange}
                                    onBlur={handleSmsVerificationCodeBlur}
                                    placeholder="Enter 6-digit code"
                                    maxLength={OTP_CODE_MAX_LENGTH}
                                    disabled={isSavingSmsPreferences}
                                    className={`mt-4 h-12 w-full rounded-xl border bg-white px-4 text-base text-dark-grey shadow-sm outline-none transition-all duration-200 placeholder:text-[#98A2B3] disabled:cursor-not-allowed disabled:opacity-70 dark:bg-darkThemeModalBg dark:text-white ${
                                        smsVerificationError
                                            ? "border-[#D92D20] focus:border-[#D92D20] focus:ring-2 focus:ring-[#D92D20]/15"
                                            : "border-[#D0D5DD] focus:border-theme focus:ring-2 focus:ring-[#FF8C65]/20"
                                    }`}
                                />
                                {smsVerificationError ? (
                                    <p className="mt-2 text-sm font-medium text-[#D92D20]" data-testid="sms-verification-error">{smsVerificationError}</p>
                                ) : (
                                    <p className="mt-3 text-sm text-dark-grey/70 dark:text-white/60">
                                        The code is valid for 10 minutes. You can resend after 1 minute and you have 5 total attempts to enter the correct verification code.
                                    </p>
                                )}
                            </div>

                            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <p className="text-sm text-dark-grey/70 dark:text-white/60">
                                    {hasRemainingVerificationAttempts
                                        ? `${MAX_VERIFICATION_ATTEMPTS - smsVerificationAttempts} verification attempts remaining.`
                                        : "You have reached the maximum number of verification attempts."}
                                </p>
                                <Button
                                    className={`text-sm font-semibold transition-colors duration-200 ${
                                        isSmsResendAvailable
                                            ? "text-theme hover:text-theme/80 cursor-pointer"
                                            : "text-dark-grey/50 dark:text-white/50 cursor-not-allowed"
                                    }`}
                                    onClick={handleResendCode}
                                    disabled={!isSmsResendAvailable}
                                    data-testid="sms-resend-code-button"
                                >
                                    {getResendButtonLabel()}
                                </Button>
                            </div>

                            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                <Button
                                    className="h-10 rounded-md px-5 text-base font-semibold leading-5 tracking-[-0.32px] text-dark-grey transition-colors duration-200 hover:text-dark-grey/80 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:hover:text-white/80"
                                    onClick={handleEditPhoneNumber}
                                    disabled={isSavingSmsPreferences}
                                    data-testid="sms-change-phone-button"
                                >
                                    Change Phone Number
                                </Button>
                                <Button
                                    className={`bg-theme px-5 py-3 text-white text-base font-semibold leading-5 tracking-[-0.32px] rounded-md min-w-32 h-10 flex items-center justify-center transition-all duration-300 ease-in-out ${
                                        canSubmitSmsVerification
                                            ? "cursor-pointer hover:bg-theme/80"
                                            : "cursor-not-allowed opacity-70"
                                    }`}
                                    onClick={handleSmsPreferencesSave}
                                    disabled={!canSubmitSmsVerification}
                                    data-testid="sms-verify-code-button"
                                >
                                    Verify Code
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
